import React, { useState } from 'react';
import { CheckCircle, Loader2, Users, TrendingUp, Award, Heart } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import api from '../utils/api';

const SKILLS = ['Teaching/Tutoring','Healthcare','IT/Technology','Legal','Finance/Accounting','Communications','Community Organizing','Logistics','Fundraising','Research','Other'];
const AREAS  = ['Education','Health','Environment','Women Empowerment','Youth Development','Livelihood','Administration','Any Area'];
const AVAIL  = ['Weekdays','Weekends','Evenings','Full-time','Remote Only'];

const Pill = ({ selected, onClick, label }) => (
  <button type="button" onClick={onClick}
    className={`px-3.5 py-1.5 rounded-full border-2 text-sm font-medium transition-all ${selected ? 'border-royal-800 bg-royal-50 text-royal-800 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300 bg-white'}`}>
    {selected ? '✓ ' : ''}{label}
  </button>
);

export default function VolunteerPage() {
  const [form, setForm] = useState({ firstName:'', lastName:'', email:'', phone:'', location:'', occupation:'', skills:[], areas:[], availability:[], experience:'', motivation:'', hours:'5' });
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const toggle = (field, val) => setForm(p => ({...p, [field]: p[field].includes(val) ? p[field].filter(x=>x!==val) : [...p[field], val]}));

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      await api.post('/volunteers', { ...form, fullName:`${form.firstName} ${form.lastName}`, name:`${form.firstName} ${form.lastName}`, status:'pending' });
      setSuccess(true);
    } catch (err) { setError(err.response?.data?.message||'Submission failed. Please try again.'); }
    finally { setLoading(false); }
  };

  const Field = ({label, children}) => (
    <div><label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>{children}</div>
  );
  const Input = (props) => <input {...props} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all" />;

  return (
    <div className="min-h-screen bg-gray-50 font-body">
      <Navbar />
      <div className="pt-20">
        <section className="relative bg-hero-gradient py-16 overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIwMjA2MCIgc3Ryb2tlLXdpZHRoPSIwLjUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] " />
          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <span className="section-tag border-gold-400/30 text-gold-300 bg-gold-500/10 mb-5">Join Our Team</span>
            <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mb-4">Become a Volunteer</h1>
            <p className="text-white/60 text-lg max-w-xl mx-auto">Share your time, skills, and passion with communities that need it most.</p>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-5 gap-10">
            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-5">
              <div className="bg-hero-gradient rounded-2xl p-6 text-white">
                <h3 className="font-display font-bold text-lg mb-4">Why Volunteer With Us?</h3>
                {[{icon:Heart,t:'Real Impact',d:'See the direct difference your work makes in communities.'},{icon:TrendingUp,t:'Skill Growth',d:'Develop professionally while doing meaningful work.'},{icon:Users,t:'Community',d:'Join a network of committed changemakers.'},{icon:Award,t:'Recognition',d:'Receive official certificates for your service.'}].map(b=>(
                  <div key={b.t} className="flex items-start gap-3 mb-4 last:mb-0">
                    <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0"><b.icon size={16} className="text-gold-400"/></div>
                    <div><div className="font-semibold text-sm">{b.t}</div><div className="text-white/50 text-xs mt-0.5">{b.d}</div></div>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-6 text-center">
                <div className="font-display text-4xl font-bold text-royal-800 mb-1">500+</div>
                <div className="text-gray-500 text-sm">Active volunteers across 12 states</div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-7">
                {success ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle size={32} className="text-emerald-600"/></div>
                    <h2 className="font-display text-2xl font-bold text-gray-900 mb-2">Application Submitted! 🎉</h2>
                    <p className="text-gray-500 mb-6">Our volunteer coordinator will review your application and reach out within 5 business days.</p>
                    <button onClick={()=>setSuccess(false)} className="btn-royal px-6 py-2.5 text-sm">Submit Another</button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="font-display font-semibold text-gray-900 text-lg">Volunteer Application</h3>
                    {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="First Name *"><Input type="text" value={form.firstName} onChange={e=>setForm(p=>({...p,firstName:e.target.value}))} required/></Field>
                      <Field label="Last Name *"><Input type="text" value={form.lastName} onChange={e=>setForm(p=>({...p,lastName:e.target.value}))} required/></Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Email *"><Input type="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} required/></Field>
                      <Field label="Phone *"><Input type="tel" value={form.phone} onChange={e=>setForm(p=>({...p,phone:e.target.value}))} required/></Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Location / State *"><Input type="text" value={form.location} onChange={e=>setForm(p=>({...p,location:e.target.value}))} required/></Field>
                      <Field label="Occupation"><Input type="text" value={form.occupation} onChange={e=>setForm(p=>({...p,occupation:e.target.value}))}/></Field>
                    </div>
                    <Field label="Skills (select all that apply)">
                      <div className="flex flex-wrap gap-2 mt-1">{SKILLS.map(s=><Pill key={s} label={s} selected={form.skills.includes(s)} onClick={()=>toggle('skills',s)}/>)}</div>
                    </Field>
                    <Field label="Program Areas of Interest">
                      <div className="flex flex-wrap gap-2 mt-1">{AREAS.map(a=><Pill key={a} label={a} selected={form.areas.includes(a)} onClick={()=>toggle('areas',a)}/>)}</div>
                    </Field>
                    <Field label="Availability">
                      <div className="flex flex-wrap gap-2 mt-1">{AVAIL.map(a=><Pill key={a} label={a} selected={form.availability.includes(a)} onClick={()=>toggle('availability',a)}/>)}</div>
                    </Field>
                    <Field label={`Hours per week: ${form.hours}h`}>
                      <input type="range" min="1" max="40" value={form.hours} onChange={e=>setForm(p=>({...p,hours:e.target.value}))} className="w-full accent-royal-800"/>
                      <div className="flex justify-between text-xs text-gray-400 mt-1"><span>1h</span><span>40h</span></div>
                    </Field>
                    <Field label="Relevant Experience">
                      <textarea rows={3} value={form.experience} onChange={e=>setForm(p=>({...p,experience:e.target.value}))} placeholder="Briefly describe any relevant experience..." className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all resize-none"/>
                    </Field>
                    <Field label="Why do you want to volunteer? *">
                      <textarea rows={4} value={form.motivation} onChange={e=>setForm(p=>({...p,motivation:e.target.value}))} required className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all resize-none"/>
                    </Field>
                    <button type="submit" disabled={loading} className="w-full btn-royal justify-center py-3.5 text-base disabled:opacity-60">
                      {loading ? <><Loader2 size={16} className="animate-spin"/>Submitting...</> : '🙋 Submit Application'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
